import {
  createRouter,
  createWebHistory,
  NavigationGuardNext,
  RouteLocationNormalized,
  RouteRecordRaw,
} from "vue-router";
import C from "@/utils/constants";
import Cookie from "@/utils/cookie";
import { useConfigStore } from "@/store/config";
import Dashboard from "../views/Dashboard.vue";
import Cell from "../views/Cell.vue";
import Login from "../views/Login.vue";
import UserManagement from "../views/UserManagement.vue";
import PasswordReset from "../views/PasswordReset.vue";
import ProgressDashboard from "@/components/progress/ProgressDashboard.vue";
import AuditTrail from "../views/AuditTrail.vue";

export const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    name: "root",
    redirect: { name: C.ROUTES.LOGIN },
  },
  {
    path: C.ROUTES.LOGIN,
    name: C.ROUTES.LOGIN,
    component: Login,
  },
  {
    path: C.ROUTES.PASSWORD_RESET,
    name: C.ROUTES.PASSWORD_RESET,
    component: PasswordReset,
    meta: { requiresAuth: true },
  },
  {
    path: C.ROUTES.DASHBOARD,
    name: C.ROUTES.DASHBOARD,
    component: Dashboard,
    meta: { requiresAuth: true },
  },
  {
    path: C.ROUTES.CELL,
    name: C.ROUTES.CELL,
    component: Cell,
    meta: { requiresAuth: true },
  },
  {
    path: C.ROUTES.USER_MANAGEMENT,
    name: C.ROUTES.USER_MANAGEMENT,
    component: UserManagement,
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: C.ROUTES.AUDIT_TRAIL,
    name: C.ROUTES.AUDIT_TRAIL,
    component: AuditTrail,
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: C.ROUTES.RBC_DASHBOARD,
    name: C.ROUTES.RBC_DASHBOARD,
    component: ProgressDashboard,
    meta: { requiresAuth: true },
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

export function navigationGuard(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
) {
  const isLogin = !!Cookie.getCookie(C.COOKIES.KEY.USER_TOKEN);
  const userRole = Cookie.getCookie(C.COOKIES.KEY.USER_ROLE);
  const isReset = Cookie.getCookie(C.COOKIES.KEY.USER_IS_RESET);
  const requiresAuth = to.matched.some((r) => r.meta?.requiresAuth);
  const requiresAdmin = to.matched.some((r) => r.meta?.requiresAdmin);

  if (!to.name) return next({ name: C.ROUTES.LOGIN });

  // If user is in password reset flow, only allow access to LOGIN and PASSWORD_RESET pages
  if (isReset === 'true') {
    if (to.name === C.ROUTES.LOGIN || to.name === C.ROUTES.PASSWORD_RESET) {
      return next();
    }
    // Redirect to password reset if trying to access other protected routes
    if (requiresAuth) {
      return next({ name: C.ROUTES.PASSWORD_RESET });
    }
  }

  // Redirect logged-in users away from login page
  if (to.name === C.ROUTES.LOGIN && isLogin && isReset !== 'true') {
    return next({ name: C.ROUTES.DASHBOARD });
  }

  // Redirect unauthenticated users away from protected routes
  if (requiresAuth && !isLogin) {
    useConfigStore().toggleLogout();
    return next({ name: C.ROUTES.LOGIN });
  }

  // Redirect non-admin users away from admin routes
  if (requiresAdmin && userRole !== "admin") {
    return next({ name: C.ROUTES.DASHBOARD });
  }

  next();
}

router.beforeEach(navigationGuard);

export default router;
